import type { TemaDaArea } from './api.js';

/**
 * O tema de uma área, aplicado como variáveis CSS.
 *
 * ## Por que num elemento, e nunca em `:root`
 *
 * Porque a pessoa troca de área sem recarregar a página — é o ponto do seletor.
 * Escrito em `:root`, o verde da área A permaneceria por baixo da área B em
 * qualquer propriedade que a segunda não declarasse, e o resultado seria uma
 * área com a cor de outro cliente. Preso ao elemento raiz do componente, o tema
 * some junto com ele.
 *
 * ## Por que o hexadecimal é revalidado aqui
 *
 * Pelo mesmo motivo documentado em `player-settings.ts`: o valor é escrito
 * **dentro de uma declaração CSS**. O servidor já valida com zod na escrita, mas
 * este arquivo não tem como saber disso em tempo de execução — e uma cadeia
 * livre chegando aqui fecharia a declaração e escreveria regra própria na página
 * do curso de alguém. Custa uma expressão regular.
 */

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

const PADRAO: TemaDaArea = {
  fundo: '#0b0b0f',
  superficie: '#16161d',
  texto: '#f4f4f5',
  textoSuave: '#a1a1aa',
  destaque: '#22c55e',
  raio: 8,
};

function cor(valor: string | undefined, reserva: string): string {
  return typeof valor === 'string' && HEX.test(valor) ? valor : reserva;
}

/** O escuro da casa sobre destaque claro, o claro da casa sobre destaque escuro. */
function sobreODestaque(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  const lin = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const luminancia =
    0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
  /* 0,179 = onde preto e branco contrastam igual; ver `textoSobre` no core. */
  return luminancia > 0.179 ? '#08130c' : '#f4f4f5';
}

/** As variáveis para pôr no `style` do elemento raiz da área. */
export function variaveisDoTema(tema?: Partial<TemaDaArea>): React.CSSProperties {
  const raio = Number.isFinite(tema?.raio)
    ? Math.min(24, Math.max(0, Number(tema!.raio)))
    : PADRAO.raio;
  const fundo = cor(tema?.fundo, PADRAO.fundo);
  const destaque = cor(tema?.destaque, PADRAO.destaque);
  /*
   * ── AS DUAS TINTAS VIVAS SAÍRAM DAQUI (01/10/2026) ───────────────────────
   *
   * `--viva` e `--viva-estreia` nasciam nesta função, ao lado de
   * `--sobre-destaque`, porque são o mesmo TIPO de valor: uma cor que o produto
   * DERIVA da escolha do cliente em vez de a copiar.
   *
   * O que estava errado não era a tese — era o SÍTIO. Esta função é da PORTA:
   * `App.tsx` e `entrada/Moldura.tsx` importam-na, e o empacotador não move
   * funções, move módulos. O `import` de `tinta-derivada.js` aqui arrastava a
   * conversão LCH inteira (**2.238 B medidos no sourcemap do pedaço de
   * entrada**) para toda tela da área — para servir `--viva`, que é lida por
   * UM ficheiro só, `agenda/tintas.ts`, que é `lazy()`. Foi o que estourou o
   * teto em 335.709 B.
   *
   * Agora quem precisa das duas é quem as faz: `variaveisVivasDaAgenda`, em
   * `agenda/tintas.ts`, aplicada no elemento raiz da Agenda POR CIMA destas
   * variáveis. A cascata faz o resto — e `tinta-derivada.ts` só é alcançável a
   * partir do pedaço da Agenda. Cercado em
   * `a-cor-do-cliente-posta-onde-se-le.test.ts`.
   */

  return {
    '--fundo': fundo,
    '--superficie': cor(tema?.superficie, PADRAO.superficie),
    '--texto': cor(tema?.texto, PADRAO.texto),
    '--texto-suave': cor(tema?.textoSuave, PADRAO.textoSuave),
    '--destaque': destaque,
    /*
     * Os três TONS de seção (leva A2), nomeados UMA vez. A seção só escolhe o
     * nome (`--tom-superficie`); ninguém escreve `color-mix` na vitrine.
     * `claro` = a superfície puxada 16% para o texto: nos dois temas é o
     * terceiro degrau que ainda se distingue da superfície, porque texto e
     * superfície já contrastam por C4.
     */
    '--tom-fundo': 'var(--fundo)',
    '--tom-superficie': 'var(--superficie)',
    '--tom-claro': 'color-mix(in srgb, var(--superficie) 84%, var(--texto) 16%)',
    /*
     * O texto SOBRE o destaque (leva L1): era `#08130c` cravado em dez lugares
     * — com um destaque azul-marinho o botão ficava ilegível. Derivado da
     * luminância do destaque, uma vez, aqui.
     */
    '--sobre-destaque': sobreODestaque(cor(tema?.destaque, PADRAO.destaque)),
    '--raio': `${raio}px`,
    backgroundColor: 'var(--fundo)',
    color: 'var(--texto)',
  } as React.CSSProperties;
}

/** `12 min`, `1h 04`, ou nada quando a duração é desconhecida. */
export function duracaoLegivel(segundos: number | null | undefined): string {
  if (!segundos || segundos <= 0) return '';
  const h = Math.floor(segundos / 3600);
  const m = Math.round((segundos % 3600) / 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}` : `${m} min`;
}

/**
 * A mesma grandeza, POR EXTENSO — para quem ouve a tela, e só para isso.
 *
 * ## Por que existe uma segunda função
 *
 * `duracao` (do core) devolve `10 h 42 min`, que é o certo no ecrã e é lido em
 * voz alta por vários leitores como «dez h quarenta e dois min». O `aria-label`
 * leva a mesma verdade em palavras: `10 horas e 42 minutos`.
 *
 * ## E por que ela NÃO é um terceiro formato
 *
 * Porque nunca aparece no ecrã. A casa já tem dois formatos visíveis a mais do
 * que devia — `duracaoLegivel` aqui (`2h 47`) e `duracao` no core
 * (`1 h 05 min`) —, e a obra da carga horária escolheu **um** deles em vez de
 * acrescentar o terceiro: tudo o que é CARGA imprime-se com `duracao`, na
 * vitrine, na sala e na frase que o tutor lê no painel, para que ninguém
 * precise de comparar dois números escritos de maneira diferente e adivinhar se
 * são o mesmo.
 *
 * Zero e nulo devolvem string vazia, como as irmãs — e quem chama trata o vazio
 * com um RAMO, nunca interpolando. «Você assistiu » com um buraco no fim é o
 * defeito que esta família de funções produz quando alguém confia nela.
 */
export function cargaPorExtenso(segundos: number | null | undefined): string {
  if (!segundos || segundos <= 0 || !Number.isFinite(segundos)) return '';
  const s = Math.round(segundos);
  const minutosTotais = Math.round(s / 60);
  if (minutosTotais < 1) return '';
  const horas = Math.floor(minutosTotais / 60);
  const minutos = minutosTotais % 60;
  const h = horas === 1 ? '1 hora' : `${horas} horas`;
  const m = minutos === 1 ? '1 minuto' : `${minutos} minutos`;
  if (horas === 0) return m;
  if (minutos === 0) return h;
  return `${h} e ${m}`;
}

/**
 * `4:32` — um **ponto** dentro da aula, e não o tamanho dela.
 *
 * ## Por que não dá para reusar `duracaoLegivel` aqui
 *
 * Porque ela arredonda para o minuto: `duracaoLegivel(272)` é `5 min`, e dizer
 * "você parou aos 5 min" de uma aula de 12 é impreciso justamente onde a
 * precisão é a informação — quem volta quer reconhecer o lugar. Pior: aos 20
 * segundos ela devolve `0 min`, que lê como "não começou".
 *
 * As duas convivem na mesma frase de propósito — `4:32 de 12 min`. A posição é
 * um relógio, porque é o mesmo número que o player mostra; a duração é uma
 * grandeza, porque a pergunta dela é "dá tempo?". Escrever as duas como relógio
 * (`4:32 de 12:07`) seria pedir uma subtração de quem só queria decidir se abre
 * a aula agora.
 */
export function posicaoLegivel(segundos: number | null | undefined): string {
  if (!segundos || segundos <= 0 || !Number.isFinite(segundos)) return '';
  /* `floor` e não `round`: arredondar para cima põe a pessoa um segundo à
     frente de onde o player vai realmente abrir. */
  const t = Math.floor(segundos);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = String(t % 60).padStart(2, '0');
  /* Os minutos só ganham zero à esquerda quando há hora antes deles: `4:32`, e
     nunca `04:32`; mas `1:04:32`, e nunca `1:4:32`. */
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}

/**
 * ── A HORA DE UMA VOZ DA CONVERSA ──────────────────────────────────────────
 *
 * `14:32` quando é de hoje, `27/09, 14:32` quando não é.
 *
 * **A data só aparece quando acrescenta alguma coisa.** Numa conversa que
 * começou há dois minutos, carimbar «29/09» em cada linha é ruído repetido
 * que empurra o que interessa; num fio retomado no dia seguinte, a hora
 * sozinha mente por omissão — «08:15» parece de agora. O corte é o dia do
 * relógio de quem lê, e é o mesmo critério que a pessoa usaria.
 *
 * Sem zero à esquerda na hora (`9:04`, não `09:04`), como `posicaoLegivel`
 * faz ali em cima — os minutos ganham o zero, a unidade maior não.
 */
export function horaDaConversa(ms: number | null | undefined, agora = Date.now()): string {
  if (!ms || !Number.isFinite(ms) || ms <= 0) return '';
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '';
  const hora = `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
  const hoje = new Date(agora);
  const mesmoDia =
    d.getFullYear() === hoje.getFullYear() &&
    d.getMonth() === hoje.getMonth() &&
    d.getDate() === hoje.getDate();
  if (mesmoDia) return hora;
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  return `${dia}/${mes}, ${hora}`;
}

/**
 * Quanto a pessoa esperou, por extenso curto — `18s`, `1min12`.
 *
 * `null` quando ainda não respondeu, e **nunca `0s`**: um turno sem resposta
 * não esperou zero, ele não acabou. É a mesma distinção entre «não sei» e
 * «zero» que o resto da casa guarda.
 */
export function esperaLegivel(de: number, ate: number | null | undefined): string | null {
  if (!ate || !Number.isFinite(ate) || ate < de) return null;
  const s = Math.round((ate - de) / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}min${String(s % 60).padStart(2, '0')}`;
}
